import { createPartnerRequest, type PartnerDto, type PartnerType } from '@zenarty/shared';
import { Factory, MapPinned, Plus, Store, Truck } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { EntityHistory } from '../components/audit';
import {
  Avatar,
  Button,
  Callout,
  EmptyState,
  Field,
  KeyValue,
  PageHeader,
  StatusBadge,
  Tabs,
} from '../components/kit';
import { Dialog, Drawer } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { formatDateTime } from '../lib/format';
import { nameMap, useAction, useClients, useServerList } from '../lib/queries';

const TABS: { id: PartnerType; label: string; icon: typeof Truck; help: string }[] = [
  {
    id: 'ship_to',
    label: 'Ship-to locations',
    icon: MapPinned,
    help: 'Retailer DCs and stores you deliver to, identified by GLN.',
  },
  {
    id: 'carrier',
    label: 'Carriers',
    icon: Truck,
    help: 'Parcel and LTL carriers, identified by SCAC. Shared by all clients.',
  },
  {
    id: 'retailer',
    label: 'Retailers',
    icon: Store,
    help: 'Retail customers whose routing guides and labels you must follow.',
  },
  {
    id: 'supplier',
    label: 'Suppliers',
    icon: Factory,
    help: 'Where inbound goods come from, per client.',
  },
];

export function PartnersPage() {
  const { can } = useAuth();
  const [type, setType] = useState<PartnerType>('ship_to');
  const clients = useClients(can('client:read'));
  const clientName = nameMap(clients.data?.items, (c) => c.name);
  const { query, server } = useServerList<PartnerDto>('partners', '/trading-partners', { type });
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const tab = TABS.find((t) => t.id === type)!;

  const columns: Column<PartnerDto>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (p) => (
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <Avatar name={p.name} />
          <div>
            <div className="primary-cell">{p.name}</div>
            <span className="code-pill">{p.code}</span>
          </div>
        </div>
      ),
    },
    ...(type === 'carrier' || type === 'retailer'
      ? []
      : [
          {
            id: 'client',
            header: 'Client',
            cell: (p: PartnerDto) =>
              p.clientId ? (
                (clientName.get(p.clientId) ?? '—')
              ) : (
                <span className="faint">All clients</span>
              ),
          },
        ]),
    {
      id: 'id',
      header: type === 'carrier' ? 'SCAC' : 'GLN',
      cell: (p) => <span className="mono">{(type === 'carrier' ? p.scac : p.gln) ?? '—'}</span>,
    },
    {
      id: 'where',
      header: 'Location',
      cell: (p) =>
        p.address ? `${p.address.city}, ${p.address.state}` : <span className="faint">—</span>,
    },
    {
      id: 'contact',
      header: 'Contact',
      cell: (p) => <span className="muted">{p.contactEmail ?? p.contactPhone ?? '—'}</span>,
    },
    { id: 'status', header: 'Status', cell: (p) => <StatusBadge status={p.status} /> },
  ];
  const open = query.data?.items.find((p) => p.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Trading partners"
        description="The carriers, retailers, ship-to locations and suppliers your orders, receipts, labels and EDI refer to."
        actions={
          can('partner:manage') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Add partner
            </Button>
          ) : null
        }
      />
      <Tabs
        value={type}
        onChange={setType}
        tabs={TABS.map((t) => ({ id: t.id, label: t.label }))}
      />
      <p className="muted small" style={{ margin: '-6px 0 12px' }}>
        {tab.help}
      </p>
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(p) => p.id}
        loading={query.isLoading}
        server={server}
        searchPlaceholder="Search name, code or GLN"
        onRowClick={(p) => setOpenId(p.id)}
        empty={<EmptyState icon={tab.icon} title={`No ${tab.label.toLowerCase()} yet`} />}
      />
      {creating ? <PartnerDialog initialType={type} onClose={() => setCreating(false)} /> : null}
      {open ? (
        <Drawer
          title={open.name}
          subtitle={<span className="code-pill">{open.code}</span>}
          icon={<Avatar name={open.name} size="lg" />}
          onClose={() => setOpenId(null)}
        >
          <section>
            <div className="section-title">Details</div>
            <KeyValue
              items={[
                ['Type', TABS.find((t) => t.id === open.type)?.label],
                ['Status', <StatusBadge key="s" status={open.status} />],
                ['Client', open.clientId ? clientName.get(open.clientId) : 'All clients'],
                ['GLN', open.gln ? <code key="g">{open.gln}</code> : null],
                ['SCAC', open.scac],
                [
                  'Address',
                  open.address
                    ? `${open.address.line1}, ${open.address.city}, ${open.address.state} ${open.address.postalCode}`
                    : null,
                ],
                ['Email', open.contactEmail],
                ['Phone', open.contactPhone],
                [
                  'Min. shelf life',
                  open.minShelfLifeDays !== null
                    ? `${open.minShelfLifeDays} days at delivery`
                    : null,
                ],
                ['Added', formatDateTime(open.createdAt)],
              ]}
            />
          </section>
          {can('audit:read') ? (
            <section>
              <div className="section-title">History</div>
              <EntityHistory entityId={open.id} />
            </section>
          ) : null}
        </Drawer>
      ) : null}
    </>
  );
}

function PartnerDialog({
  initialType,
  onClose,
}: {
  initialType: PartnerType;
  onClose: () => void;
}) {
  const clients = useClients();
  const [f, setF] = useState({
    type: initialType,
    clientId: '',
    code: '',
    name: '',
    gln: '',
    scac: '',
    line1: '',
    city: '',
    state: '',
    postalCode: '',
    contactEmail: '',
    contactPhone: '',
    minShelfLifeDays: '',
  });
  const [problems, setProblems] = useState<string[]>([]);
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const save = useAction(
    (body: unknown) => api<PartnerDto>('/trading-partners', { method: 'POST', body }),
    {
      invalidate: [['partners']],
      success: 'Partner added',
      onSuccess: onClose,
    },
  );
  const submit = () => {
    const hasAddress = f.line1 || f.city || f.state || f.postalCode;
    const body = {
      type: f.type,
      clientId: f.type === 'carrier' || !f.clientId ? null : f.clientId,
      code: f.code,
      name: f.name,
      gln: f.gln.trim() || null,
      scac: f.scac.trim() || null,
      address: hasAddress
        ? {
            line1: f.line1,
            city: f.city,
            state: f.state.toUpperCase(),
            postalCode: f.postalCode,
            country: 'US',
          }
        : null,
      contactEmail: f.contactEmail.trim() || null,
      contactPhone: f.contactPhone.trim() || null,
      minShelfLifeDays:
        (f.type === 'retailer' || f.type === 'ship_to') && f.minShelfLifeDays.trim()
          ? Number(f.minShelfLifeDays)
          : null,
    };
    const parsed = createPartnerRequest.safeParse(body);
    if (!parsed.success) {
      setProblems(parsed.error.issues.map((i) => `${i.path.join('.') || 'partner'}: ${i.message}`));
      return;
    }
    setProblems([]);
    save.mutate(parsed.data);
  };
  return (
    <Dialog
      title="Add a trading partner"
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Add partner
          </Button>
        </>
      }
    >
      {problems.length ? (
        <Callout tone="danger" title="Check these fields">
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      <div className="form-grid">
        <Field label="Type">
          <select className="select" value={f.type} onChange={(e) => set('type', e.target.value)}>
            {TABS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label.replace(/s$/, '')}
              </option>
            ))}
          </select>
        </Field>
        {f.type !== 'carrier' ? (
          <Field label="Client" hint="Leave empty to share with all clients">
            <select
              className="select"
              value={f.clientId}
              onChange={(e) => set('clientId', e.target.value)}
            >
              <option value="">All clients</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <div />
        )}
        <Field label="Name">
          <input className="input" value={f.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Code">
          <input
            className="input mono"
            value={f.code}
            onChange={(e) => set('code', e.target.value.toUpperCase())}
          />
        </Field>
        {f.type === 'carrier' ? (
          <Field label="SCAC" hint="Standard Carrier Alpha Code">
            <input
              className="input mono"
              value={f.scac}
              onChange={(e) => set('scac', e.target.value.toUpperCase())}
            />
          </Field>
        ) : (
          <Field label="GLN" hint="13-digit Global Location Number">
            <input
              className="input mono"
              value={f.gln}
              onChange={(e) => set('gln', e.target.value)}
            />
          </Field>
        )}
        <Field label="Contact email">
          <input
            className="input"
            type="email"
            value={f.contactEmail}
            onChange={(e) => set('contactEmail', e.target.value)}
          />
        </Field>
        {f.type === 'retailer' || f.type === 'ship_to' ? (
          <Field
            label="Minimum shelf life (days)"
            hint="Lots with less life left at delivery are never allocated to this partner"
          >
            <input
              className="input num"
              inputMode="numeric"
              value={f.minShelfLifeDays}
              onChange={(e) => set('minShelfLifeDays', e.target.value)}
            />
          </Field>
        ) : null}
        <Field label="Street" className="span-2">
          <input className="input" value={f.line1} onChange={(e) => set('line1', e.target.value)} />
        </Field>
        <Field label="City">
          <input className="input" value={f.city} onChange={(e) => set('city', e.target.value)} />
        </Field>
        <div className="form-grid" style={{ gap: 12 }}>
          <Field label="State">
            <input
              className="input"
              maxLength={2}
              value={f.state}
              onChange={(e) => set('state', e.target.value)}
            />
          </Field>
          <Field label="ZIP">
            <input
              className="input"
              value={f.postalCode}
              onChange={(e) => set('postalCode', e.target.value)}
            />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
